import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PFieldAbstractComponent } from '../p-field-abstract';
import { PInputSize } from './p-input-size.enum';
import { ErrorStateMatcher } from '@angular/material/core';
import { KeyboardService } from '../../keyboard';

@Component({
    selector: 'p-field-numeric',
    template: `
        <p-field
            [isMandatory]="isMandatory"
            [label]="label"
            [labelTooltip]="labelTooltip"
            [isReadOnly]="isReadOnly"
            [isDisable]="isDisabled"
            [shouldTransformLabelTextCase]="shouldTransformLabelTextCase"
            [labelOnLeft]="labelOnLeft"
        >
            <div class="p-input-numeric-container" [class.no-error-message]="!errorMessage" slot="editMode">
                <mat-form-field class="p-input-numeric" appearance="fill" [ngClass]="classes" floatLabel="always">
                    <input
                        matInput
                        [attr.automation-id]="'p-field-numeric_' + label"
                        #pInput
                        [value]="value"
                        [type]="'number'"
                        [placeholder]="placeholder | sentenceCase"
                        [readonly]="isReadOnly"
                        [attr.aria-label]="ariaLabel"
                        [errorStateMatcher]="matcher"
                        autocomplete="off"
                        onfocus="this.select()"
                        class="p-input-numeric-input"
                        [max]="max"
                        [min]="min"
                        [step]="step"
                        (change)="changeValue($event)"
                        (input)="onInput($event)"
                        (keydown)="onKeydownEvent($event)"
                        (keydown.tab)="$event.stopImmediatePropagation()"
                        (keydown.arrowdown)="$event.stopImmediatePropagation()"
                        (keydown.arrowup)="$event.stopImmediatePropagation()"
                    />
                    <mat-error *ngIf="isValid" class="p-input-numeric-error">
                        {{ errorMessage }}
                    </mat-error>
                </mat-form-field>
            </div>
            <div slot="readMode">
                <mat-form-field
                    class="p-input-text"
                    appearance="fill"
                    [class.p-input-text-readonly]="isReadOnly"
                    [ngClass]="classes"
                    floatLabel="always"
                >
                    <input
                        matInput
                        [attr.automation-id]="'p-field-numeric_' + label"
                        readonly
                        [value]="value"
                        [disabled]="isDisabled"
                        [attr.aria-label]="ariaLabel"
                        class="p-input-text-input"
                    />
                </mat-form-field>
            </div>
        </p-field>
    `,
    styleUrls: ['./p-field-numeric.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldNumericComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldNumeric'
})
export class PFieldNumericComponent extends PFieldAbstractComponent<number> implements AfterViewInit {
    @Input() override ariaLabel?: string;
    @Input() errorMessage?: string;
    @Input() size = PInputSize.Large;
    @Input() matcher: ErrorStateMatcher = {
        // Implemented the default behavior described in Angular docs:
        // https://material.angular.io/components/input/overview#changing-when-error-messages-are-shown
        isErrorState: (control, form) => !!control && control.invalid && (control.touched || (!!form && form.submitted))
    };
    @Input() label?: string;
    @Input() isMandatory = false;
    @Input() max: number = 99999999;
    @Input() min: number = -99999999;
    @Input() digitsAfterPoint: number = -1;
    @Input() step: number = 1;
    @Input() placeholder?: string;
    @Input() autoFocus = false;
    @Input() hasInputListener = false;
    @Input() enableFraction = false;
    @Input() public labelOnLeft = true;

    @ViewChild('pInput', { static: true }) pInput: ElementRef<HTMLInputElement> = {} as ElementRef;

    constructor(cd: ChangeDetectorRef, private keyboardService: KeyboardService) {
        super(cd);
    }

    public get classes(): string[] {
        const classParams = [];
        if (this.isReadOnly) {
            classParams.push('p-input-numeric-readonly');
        }
        classParams.push(`p-input-numeric-${this.size}`);
        return classParams;
    }

    changeValue(ev: Event | any) {
        this.markAsTouched();

        if (this.max && ev.target?.value > this.max) {
            ev.target.value = this.max;
        } else if (this.digitsAfterPoint && this.digitsAfterPoint > 0 && ev.target.value) {
            let numRegex = new RegExp('^\\d+[.]?\\d{0,' + this.digitsAfterPoint + '}$');
            if (!numRegex.test(ev.target.value)) {
                let currentValue: number = +ev.target.value;
                ev.target.value = currentValue.toFixed(this.digitsAfterPoint);
            }
        }

        if (!this.isReadOnly) {
            this.value = ev.target.value;
            this._onChange(this?.value);
        }
    }

    ngAfterViewInit(): void {
        if (this.autoFocus) {
            this.pInput.nativeElement.focus();
        }
    }

    onInput(ev: Event) {
        if (this.hasInputListener) {
            this.changeValue(ev);
        }
    }

    onKeydownEvent(e: KeyboardEvent) {
        const code = e.keyCode;

        return (
            this.keyboardService.isTab(code) ||
            this.keyboardService.isDown(code) ||
            this.keyboardService.isUp(code) ||
            this.keyboardService.isNumber(code) ||
            this.keyboardService.isBackspace(code) ||
            (this.keyboardService.isMinus(code) && this.isMinusAllowed()) ||
            this.keyboardService.isDelete(code) ||
            this.keyboardService.isLeft(code) ||
            this.keyboardService.isRight(code) ||
            (this.keyboardService.isDot(code) && this.enableFraction)
        );
    }

    private isMinusAllowed() {
        return this.min < 0 || this.min === undefined;
    }
}
